import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const TOTAL_SEATS = 100;

export default function Founders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/founders");
        setData(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const seats = Array.from({ length: TOTAL_SEATS }, (_, i) => {
    return data?.founders?.[i] || null;
  });

  return (
    <div className="min-h-screen relative z-10" data-testid="founders-page">
      <header className="border-b border-amber-500/20 backdrop-blur-md bg-stone-950/60 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif-display gold text-lg tracking-widest" data-testid="brand-link">MYTHICMATRIX</Link>
          <div className="font-mono text-[0.6rem] uppercase tracking-widest text-stone-500">The Founders' Wall</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16 space-y-10">
        <section className="max-w-3xl space-y-4">
          <div className="font-mono text-[0.6rem] uppercase tracking-[0.4em] gold">Charter of the First Hundred</div>
          <h1 className="font-serif-display text-3xl sm:text-5xl leading-tight">The Founding Sovereigns</h1>
          <p className="font-prose text-stone-300 leading-relaxed">
            The first hundred souls to seal the covenant. Their names are kept in the archive, marked with an eternal
            <span className="gold"> ✦ </span> beside every world they craft, every rating they cast, every chronicle they finish.
          </p>
          {data && (
            <div className="flex items-center gap-3 pt-2 font-mono text-[0.65rem] uppercase tracking-widest text-stone-500">
              <div className="h-1 flex-1 bg-stone-900 rounded-sm overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all"
                  style={{ width: `${(data.claimed / TOTAL_SEATS) * 100}%` }}
                  data-testid="founders-progress"
                />
              </div>
              <span data-testid="founders-count">
                <span className="gold">{data.claimed}</span> / {TOTAL_SEATS} sealed · {data.remaining} charters remain
              </span>
            </div>
          )}
        </section>

        {loading && (
          <div className="font-mono text-xs text-stone-500 animate-pulse">Consulting the archive...</div>
        )}
        {err && (
          <div className="font-mono text-xs text-red-400">{err}</div>
        )}

        {!loading && !err && (
          <section className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {seats.map((f, i) => (
              <Seat key={i} index={i} founder={f} />
            ))}
          </section>
        )}

        <section className="pt-4 border-t border-amber-500/10 flex items-center justify-between flex-wrap gap-3">
          <Link to="/" className="btn-ghost text-[0.65rem]" data-testid="back-home">← Return to the Tome</Link>
          {data?.remaining > 0 && (
            <Link to="/app?upgrade=1" className="btn-gold" data-testid="claim-charter-cta">Claim a Charter · $49</Link>
          )}
        </section>
      </main>
    </div>
  );
}

function Seat({ index, founder }) {
  const pos = String(index + 1).padStart(3, "0");
  if (!founder) {
    return (
      <div
        data-testid={`founder-seat-${index}`}
        data-claimed="false"
        className="rounded-sm border border-dashed border-stone-700 bg-stone-900/30 p-3 h-24 flex flex-col justify-between"
      >
        <div className="font-mono text-[0.55rem] uppercase tracking-widest text-stone-600">№ {pos}</div>
        <div className="font-serif-body italic text-stone-600 text-sm">— vacant —</div>
        <div className="font-mono text-[0.55rem] uppercase tracking-widest text-stone-600">Charter open</div>
      </div>
    );
  }
  const dateStr = founder.founded_at
    ? new Date(founder.founded_at).toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })
    : "";
  return (
    <div
      data-testid={`founder-seat-${index}`}
      data-claimed="true"
      className="rounded-sm border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-stone-900/60 to-stone-900/30 p-3 h-24 flex flex-col justify-between shadow-[inset_0_0_20px_-8px_rgba(212,175,55,0.35)]"
    >
      <div className="flex items-center justify-between font-mono text-[0.55rem] uppercase tracking-widest gold">
        <span>№ {pos}</span>
        <span title="Founding Sovereign">✦</span>
      </div>
      <div className="font-serif-body text-stone-100 text-sm leading-tight truncate" title={founder.name}>
        {founder.name}
      </div>
      <div className="font-mono text-[0.55rem] uppercase tracking-widest text-stone-500">{dateStr}</div>
    </div>
  );
}
